import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../utils/api'

interface Announcement {
  id: number
  title: string
  content: string
  type: 'feature' | 'improvement' | 'bug_fix' | 'maintenance'
  created_at: string
  created_by: string
}

const AnnouncementsFeed: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchAnnouncements()
  }, [])

  const fetchAnnouncements = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/announcements`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        const announcementsArray = Array.isArray(data) ? data : (data.announcements || [])
        setAnnouncements(announcementsArray)
      }
    } catch (error) {
      console.error('Error fetching announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature': return 'bg-gradient-to-r from-gray-800 to-gray-900 text-white'
      case 'improvement': return 'bg-gradient-to-r from-green-500 to-green-600 text-white'
      case 'bug_fix': return 'bg-gradient-to-r from-red-500 to-red-600 text-white'
      case 'maintenance': return 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white'
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'feature': return '✨'
      case 'improvement': return '📈'
      case 'bug_fix': return '🔧'
      case 'maintenance': return '🛠️'
      default: return '📢'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-gray-900 border-r-transparent"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30 dark:from-gray-900 dark:via-purple-900/10 dark:to-pink-900/10">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2 flex items-center transition-colors"
              >
                ← {t('common.backToDashboard')}
              </button>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-pink-600 bg-clip-text text-transparent">
                {t('dashboard.announcements.pageTitle')}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {t('dashboard.announcements.pageSubtitle')}
              </p>
            </div>
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-gradient-to-r from-gray-800 to-pink-500 rounded-2xl flex items-center justify-center text-3xl shadow-lg">
                📢
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {announcements.length > 0 ? (
          <div className="space-y-6">
            {announcements.map((announcement, index) => (
              <article
                key={announcement.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700"
              >
                {/* Article Header */}
                <div className="p-6 md:p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${getTypeColor(announcement.type)} shadow-sm`}>
                          <span>{getTypeIcon(announcement.type)}</span>
                          <span>{t(`dashboard.announcements.types.${announcement.type}`)}</span>
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(announcement.created_at)}
                        </span>
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                        {announcement.title}
                      </h2>
                    </div>
                  </div>

                  {/* Article Content */}
                  <div className="prose prose-lg dark:prose-invert max-w-none">
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {announcement.content}
                    </p>
                  </div>

                  {/* Article Footer */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-800 to-pink-500 flex items-center justify-center text-white font-semibold text-xs">
                          {announcement.created_by.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <span>
                          {t('dashboard.widgets.by')} <span className="font-medium text-gray-900 dark:text-white">{announcement.created_by}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center border border-gray-200 dark:border-gray-700">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-gray-800 to-pink-500 rounded-full flex items-center justify-center text-4xl">
              📭
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('dashboard.announcements.noAnnouncementsTitle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t('dashboard.announcements.noAnnouncementsMessage')}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-5xl mx-auto px-4 py-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('dashboard.announcements.footerMessage')}
        </p>
      </div>
    </div>
  )
}

export default AnnouncementsFeed

